"use strict";

const globals = require("js/globals");
const eventstream = require("js/eventstream");

const MODULE = "ldc-sub-editor";
const EDITOR_ID = "editor-id";

class LDCAbstractEditor extends HTMLElement {
    constructor(name) {
        super();

        // every editor has a unique id (simple number) for the current session. This is
        // mainly used to identify the memento of this specific editor, in order
        // to check if the state of the editor has changed compared to the memento
        this.__editorId = globals.getStorage(MODULE, EDITOR_ID, 0);
        globals.setStorage(MODULE, EDITOR_ID, this.__editorId + 1);

        this.__name = name;

        // we assume that the container has not been scrolled when the component is constructed
        this.__containerScrollTop = 0;

        this.__SUBSCRIPTIONS = {
            showInPartner: {
                name: "SHOW_IN_PARTNER",
                stream: (this.__openInPartnerView = eventstream.eventSource()),
            },
        };

        this.SUBSCRIPTION_TARGETS = {};
        Object.keys(this.__SUBSCRIPTIONS).forEach(
            (key) => (this.SUBSCRIPTION_TARGETS[key] = this.__SUBSCRIPTIONS[key].name),
        );
    }

    name() {
        return this.__name;
    }

    mementoFromState() {
        return this.__enrichMemento(this.__mementoFromState());
    }

    mementoFromCardContent(card, content) {
        return this.__enrichMemento(this.__mementoFromCardContent(card, content));
    }

    __enrichMemento(memento) {
        memento.editorName = this.__name;
        memento.editorId = this.__editorId;
        memento.hasBeenMerged = false;
        return memento;
    }

    setState(memento) {
        // assert memento.editorName == this.__name

        // we add the editorId here, so that we can find the memento state for this editor
        // in a later hasBeenModified check
        memento.editorId = this.__editorId;
        this.__setState(memento);
    }

    hasBeenModified(mementos) {
        const memento = mementos.find((editorMemento) => editorMemento.editorId === this.__editorId);
        if (memento != undefined) {
            return memento.hasBeenMerged || this.__hasBeenModified(memento);
        } else {
            console.log("hasBeenModified returns true because we don't find the memento");
            // if we cannot find a memento for this editor, this means that the editor has probably
            // been initialised in this session, so there definitley is a modification here
            return true;
        }
    }

    getContentForSaving(cardType) {
        const content = this.__doGetContentForSaving(cardType);
        return Array.isArray(content) ? content : [content];
    }

    /*
     * Called everytime the container of the editor is being scrolled.
     *
     * The function will request an animation frame from the browser in which the method __onContainerScroll
     * is being executed.
     *
     * This can be used by subclasses to reposition any elements that are positioned absolute with respect to
     * the document root/body
     */
    containerScroll(containerScrollTop) {
        this.__containerScrollTop = containerScrollTop;
        window.requestAnimationFrame(this.__onContainerScroll.bind(this));
    }

    /*
     * Will merge the content of the passed instance into this editor.
     *
     * Editors must have the same name for this operation to work, otherwise an error will be thrown.
     *
     * @return "merged" if the stuff was merged, "no-merge" if the editors are not mergable
     */
    merge(next) {
        if (next.name() == this.name()) {
            const mergeResult = this.__doMerge(next);

            if (mergeResult == "merged") {
                this.__memento.hasBeenMerged = true;
            }

            return mergeResult;
        } else {
            return "unmergable";
        }
    }

    subscribe(target, callback) {
        const to = this.__getStream(target);

        if (to == null) {
            throw new Error("Unknown subscription target: " + target);
        } else {
            return to.stream.subscribe(callback);
        }
    }

    __notify(target, payload) {
        const to = this.__getStream(target);

        if (to == null) {
            throw new Error("Unknown subscription target: " + target);
        } else {
            to.stream.notify(payload);
        }
    }

    __getStream(target) {
        const to = Object.keys(this.__SUBSCRIPTIONS).find((key) => this.__SUBSCRIPTIONS[key].name == target);
        return this.__SUBSCRIPTIONS[to];
    }

    /************************************************************
     * Abstract methods that _can_ be implemented by subclasses *
     ************************************************************/

    /*
     * You can use this function to adjust any absolutely positioned controls that are positioned
     * relative to the body/document root, so that they seem to be scrolling as well. Note that you must
     * check the this.__containerScrollTop variable to get the current scrollTop value
     */
    __onContainerScroll() {
        // pass
    }

    cardTypeChanged() {
        // pass
    }

    /*************************************************************
     * Abstract methods that _must be_ implemented by subclasses *
     *************************************************************/

    /**
     * revers engineering comments:
     * return an object, which should contain a key: 'type' which is used to match on the server side.
     * this map is sent to the server
     * @return an object
     */
    __doGetContentForSaving(cardType) {
        throw new Error("__doGetContentForSaving(): This method is abstract and must be implemented by subclasses!");
    }

    /**
     * revers engineering comments:
     * convert the current statue of this object to a memento
     * @return an object
     */
    __mementoFromState() {
        throw new Error("__mementoFromState(): This method is abstract and must be implemented by subclasses!");
    }

    /**
     * revers engineering comments:
     * this is called with the server-side-card-content and you have convert to a memento
     * @return an object
     */
    __mementoFromCardContent(card, content) {
        throw new Error("__mementoFromCardContent(): This method is abstract and must be implemented by subclasses!");
    }

    __setState(memento) {
        throw new Error("__setState(): This method is abstract and must be implemented by subclasses!");
    }

    clearState() {
        throw new Error("clearState(): This method is abstract and must be implemented by subclasses!");
    }

    /**
     * revers engineering comments:
     * this one is used to offer th "Do you want to cancel" option
     * @return bool
     */
    __hasBeenModified(memento) {
        throw new Error("__hasBeenModified(): This method is abstract and must be implemented by subclasses!");
    }

    focus() {
        throw new Error("focus(): This method is abstract and must be implemented by subclasses!");
    }

    /**
     * revers engineering comments:
     * returns an object with the default-values
     */
    getEmptyMemento() {
        throw new Error("getEmptyMemento(): This method is abstract and must be implemented by subclasses!");
    }

    /*
     * Must either merge the passed instance into this instance, or decide that these kind of editors
     * are not mergable.
     *
     * @return "merged" if the stuff was merged, "no-merge" if the editors are not mergable
     */
    __doMerge(next) {
        throw new Error("This method is abstract and must be implemented by subclasses!");
    }

    /**
     * @param newCardId
     */
    callBackAfterNewCardCreated(newCardId) {
        // might be implemented
    }
}

export { LDCAbstractEditor };
